/**
 * Sentry capture for DICOM viewer failures.
 *
 * Two capture layers:
 *  - A central listener on Cornerstone's global eventTarget for per-image load
 *    failures (`subscribeToImageLoadErrors`, wired up in init.ts). This covers
 *    setStack frame loads, prefetch, scroll-time lazy loads and volume
 *    streaming — including paths that have no catch site in our code.
 *  - Explicit `captureViewerError` calls from viewport catch sites (stack /
 *    MPR / fusion / video) for setup-level failures the user actually sees,
 *    with series/study context the event listener lacks.
 *
 * All verbosity decisions live in VIEWER_CAPTURE_CONFIG below — dial back
 * there, never at call sites.
 */
import { normalizeEndpointKey } from '@/lib/api/capture-error';
import { isDeliberateAbort } from '@/lib/localGateway/abortedRequests';
import { Enums, eventTarget } from '@cornerstonejs/core';
import * as Sentry from '@sentry/react';
// ── DIAL-BACK KNOBS: the single place to adjust viewer-Sentry verbosity ──
// Generous during live-client onboarding; first candidate to disable once
// confident is 'image-load' (leaving only user-visible error-level events).
export const VIEWER_CAPTURE_CONFIG = {
    // 'webgl' is context LOSS — every viewport in the engine goes blank at once
    // and, without our preventDefault, permanently. It is never benign and never
    // noisy (a session sees it zero times or once), so it stays on at error level.
    enabled: { stack: true, 'image-load': true, mpr: true, fusion: true, video: true, webgl: true },
    level: {
        stack: 'error',
        'image-load': 'warning',
        mpr: 'error',
        fusion: 'error',
        video: 'error',
        webgl: 'error',
    },
    maxEventsPerKey: 1, // Sentry events per dedup key per session
    maxEventsPerSession: 25, // hard cap across all viewer categories
};
/**
 * 'wadouri:https://h/api/dicom/instances/<uuid>/file?frame=3'
 *   → 'https://h/api/dicom/instances/:id/file'
 * One endpoint = one Sentry issue, regardless of instance/frame.
 */
export function normalizeImageId(imageId) {
    const withoutScheme = imageId.replace(/^[a-z][a-z0-9+.-]*:(?=https?:\/\/)/i, '');
    // Production imageIds are signed storage URLs whose object id is embedded in
    // the filename ('.../dicom/<uuid>.dcm'), which normalizeEndpointKey's
    // whole-segment test misses — collapse GUID/long-hex runs anywhere in the
    // path first so per-instance ids never leak into fingerprints or dedup keys.
    const idsCollapsed = withoutScheme.replace(/[0-9a-f]{8}(-[0-9a-f]{4,}){1,4}|[0-9a-f]{16,}/gi, ':id');
    return normalizeEndpointKey(idsCollapsed);
}
/**
 * Expected/benign rejections that must never reach Sentry:
 * cache backpressure (CACHE_SIZE_EXCEEDED pauses prefetch by design) and
 * abort/cancel-style rejections from viewport teardown or navigation mid-load.
 */
export function isBenignViewerError(error) {
    if (error instanceof Error && error.name === 'AbortError')
        return true;
    const message = error instanceof Error ? error.message : String(error);
    if (message.includes('CACHE_SIZE_EXCEEDED'))
        return true;
    return /\b(abort|cancel)/i.test(message);
}
/**
 * Signed storage URLs carry bearer query params (Signature/KeyName/Expires) —
 * never export those to Sentry. Keeping origin+path still identifies the exact
 * failing object (instance ids), which the :id-normalized endpoint tag hides.
 */
function redactImageId(imageId) {
    return imageId.split('?')[0];
}
// Keys whose values are large payloads or parsed DICOM data (PHI) — never
// serialize their contents into a Sentry event. dataSet is dicomParser's
// DataSet (whole-file byteArray + parsed elements) from wadouri rejections.
const HEAVY_OR_SENSITIVE_KEYS = new Set(['dataSet', 'byteArray', 'request', 'response']);
function safeStringify(value) {
    if (typeof value === 'string')
        return value;
    try {
        const json = JSON.stringify(value, (key, val) => {
            if (HEAVY_OR_SENSITIVE_KEYS.has(key))
                return undefined;
            // Typed arrays / buffers reached via any other key: drop before
            // JSON.stringify walks millions of numeric entries.
            if (ArrayBuffer.isView(val) || val instanceof ArrayBuffer)
                return undefined;
            return val;
        });
        if (json && json !== '{}')
            return json.slice(0, 300);
    }
    catch {
        // circular structure — fall through
    }
    return String(value);
}
/**
 * The Cornerstone dicom-image-loader does not reject with Errors:
 * xhrRequest rejects with the raw XMLHttpRequest, and wadouri/loadImage wraps
 * that again as { error, dataSet }. Left as-is those reach Sentry as
 * "Error: [object Object]". Unwrap and convert to something readable.
 */
export function normalizeViewerError(error, depth = 0) {
    if (error instanceof Error)
        return { err: error };
    if (typeof error === 'string')
        return { err: new Error(error) };
    if (error && typeof error === 'object' && depth < 3) {
        // wadouri loadImage rejection shape: { error, dataSet }
        const inner = error.error;
        if (inner !== undefined && inner !== error) {
            return normalizeViewerError(inner, depth + 1);
        }
        // xhrRequest rejects with the XMLHttpRequest itself
        const xhr = error;
        if (typeof xhr.status === 'number' && typeof xhr.readyState === 'number') {
            const statusText = xhr.statusText ? ` ${xhr.statusText}` : '';
            return {
                err: new Error(`Image request failed: HTTP ${xhr.status}${statusText}`),
                httpStatus: xhr.status,
                aborted: xhr.status === 0 && xhr.readyState === 0,
            };
        }
        const message = error.message;
        if (typeof message === 'string' && message) {
            return { err: new Error(message) };
        }
    }
    return { err: new Error(safeStringify(error)) };
}
// Session-scoped dedup: occurrences per key, and total events actually sent.
const occurrencesByKey = new Map();
let eventsSent = 0;
// Suppressed-failure counts still matter (is it 2 frames or 300?) — surface
// them as breadcrumbs at these thresholds so they ride along on replays and
// any later captured event without spending quota.
const BREADCRUMB_THRESHOLDS = [2, 10, 50, 100];
export function resetViewerErrorDedupForTests() {
    occurrencesByKey.clear();
    eventsSent = 0;
}
export function captureViewerError(error, info) {
    if (!VIEWER_CAPTURE_CONFIG.enabled[info.context])
        return;
    const { err, httpStatus, aborted } = normalizeViewerError(error);
    // Check both the raw value and the normalized Error: CACHE_SIZE_EXCEEDED /
    // abort markers may live on either side of the loader's wrapper objects.
    if (isBenignViewerError(error) || isBenignViewerError(err))
        return;
    // Intentionally-aborted XHRs (viewer teardown cancels in-flight prefetch
    // XHRs) carry nothing actionable — mirrors the beforeSend guard in
    // lib/sentry.ts for the unhandled-rejection path. Status-0 *network*
    // failures (readyState DONE) are real outages and stay reportable.
    if (aborted)
        return;
    const normalizedImageId = info.imageId ? normalizeImageId(info.imageId) : undefined;
    const key = `${info.context}:${info.seriesUid ?? normalizedImageId ?? err.name}`;
    const occurrences = (occurrencesByKey.get(key) ?? 0) + 1;
    occurrencesByKey.set(key, occurrences);
    const suppressed = occurrences > VIEWER_CAPTURE_CONFIG.maxEventsPerKey ||
        eventsSent >= VIEWER_CAPTURE_CONFIG.maxEventsPerSession;
    if (suppressed) {
        // react-doctor-disable-next-line react-doctor/js-set-map-lookups -- Array.includes membership check on a 4-element constant
        if (BREADCRUMB_THRESHOLDS.includes(occurrences)) {
            Sentry.addBreadcrumb({
                category: 'viewer',
                level: 'warning',
                message: `${key} failed ${occurrences} times (suppressed)`,
            });
        }
        return;
    }
    eventsSent++;
    Sentry.captureException(err, {
        level: VIEWER_CAPTURE_CONFIG.level[info.context],
        tags: {
            area: 'viewer',
            viewer_context: info.context,
            ...(info.seriesUid ? { series_uid: info.seriesUid } : {}),
            ...(info.studyId ? { study_id: info.studyId } : {}),
            ...(info.modality ? { modality: info.modality } : {}),
            ...(normalizedImageId ? { image_endpoint: normalizedImageId } : {}),
            ...(httpStatus !== undefined ? { http_status: String(httpStatus) } : {}),
        },
        contexts: {
            viewer: {
                ...(info.imageId ? { imageId: redactImageId(info.imageId) } : {}),
                ...(info.imageCount !== undefined ? { imageCount: info.imageCount } : {}),
                // No rawError echo of non-Error rejections: the normalized message is
                // always derived from the raw value, and loader wrappers can carry
                // whole DICOM datasets that must never be serialized into an event.
                ...info.extra,
            },
        },
        // Per-image errors all throw from the same Cornerstone loader frames, so
        // stack-based grouping would collapse everything into one issue. Group per
        // context + normalized endpoint + error type instead. Raw seriesUid stays a
        // tag (drill-down) but out of the fingerprint so every broken series of the
        // same failure type lands in one issue.
        fingerprint: [
            'viewer',
            info.context,
            normalizedImageId ?? (info.seriesUid ? 'series' : 'unknown'),
            err.name,
        ],
    });
}
// Cornerstone emits per-image failures as two events on the global eventTarget:
//  - IMAGE_LOAD_FAILED from imageLoader (loadImage/loadAndCacheImage rejections,
//    detail: { imageId, error }) — covers prefetch and direct loads.
//  - IMAGE_LOAD_ERROR from StackViewport / streaming volumes
//    (detail: { imageId, imageIdIndex, error }) — covers frame display and
//    volume streaming failures.
// A failing stack frame fires BOTH; the dedup key (normalized endpoint)
// collapses the duplicate to a single Sentry event.
let subscribed = false;
function handleImageLoadEvent(evt) {
    const detail = evt.detail;
    if (!detail)
        return;
    // StackViewport.loadImages dispatches the rejected-results array directly.
    // Defensive path: these failures also fire the object-shape event per image,
    // so dedup collapses the overlap. Loader rejections sometimes carry the
    // imageId on the reason — use it when present so grouping stays per-endpoint.
    if (Array.isArray(detail)) {
        for (const item of detail) {
            const imageId = item.reason?.imageId;
            captureViewerError(item.reason, { context: 'image-load', imageId });
        }
        return;
    }
    // Requests the app aborted itself (MPR teardown / retry) are not failures.
    if (isDeliberateAbort(detail.error))
        return;
    captureViewerError(detail.error, { context: 'image-load', imageId: detail.imageId });
}
/** Subscribe once (idempotent) — called from Cornerstone init. */
export function subscribeToImageLoadErrors() {
    if (subscribed)
        return;
    subscribed = true;
    eventTarget.addEventListener(Enums.Events.IMAGE_LOAD_FAILED, handleImageLoadEvent);
    eventTarget.addEventListener(Enums.Events.IMAGE_LOAD_ERROR, handleImageLoadEvent);
}
